import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Container,
  CircularProgress,
  Typography,
  TextField,
  IconButton,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

// 使用CDN上的Worker文件
GlobalWorkerOptions.workerSrc = 
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js";

const PDFExtractPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile?.type === 'application/pdf') {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please select a PDF file');
    }
  };

  const handleExtract = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument(arrayBuffer).promise;
      let extractedContent = '';
      
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        extractedContent += textContent.items
          .map(item => item.str)
          .join(' ')
          + '\n\n'; // 分页符
      }

      setExtractedText(extractedContent);
    } catch (err) {
      setError('解析失败: ' + err.message);
      console.error('PDF解析错误:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <IconButton 
          onClick={() => navigate('/')}
          sx={{ 
            color: '#1976d2',
            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
          }}
        >
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600, 
            color: '#1565c0',
            textShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          PDF Content Extractor
        </Typography>
      </Stack>

      <Card sx={{ 
        p: 4, 
        mb: 4,
        borderRadius: 2,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)'
        }
      }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFileIcon />}
            sx={{ 
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: 16,
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            Choose PDF File
            <input
              type="file"
              hidden
              accept="application/pdf"
              onChange={handleFileUpload}
            />
          </Button>
          
          {file && (
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              Selected: {file.name}
            </Typography>
          )}
        </Box>

        {error && (
          <Typography 
            color="error" 
            sx={{ 
              mt: 2, 
              fontWeight: 500,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          onClick={handleExtract}
          disabled={!file || loading}
          sx={{ 
            mt: 3, 
            px: 4, 
            py: 1.5,
            fontSize: 16,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          }}
        >
          {loading ? <CircularProgress size={24} /> : 'Extract Content'}
        </Button>
      </Card>

      {extractedText && (
        <Card sx={{ 
          p: 4, 
          borderRadius: 2,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              color: '#1976d2',
              mb: 3
            }}
          >
            Extraction Results
          </Typography>
          <TextField
            fullWidth
            multiline
            variant="outlined"
            minRows={6}
            value={extractedText}
            InputProps={{
              readOnly: true,
              sx: { 
                fontFamily: 'monospace', 
                fontSize: 14,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                padding: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 1
              }
            }}
          />
        </Card>
      )}
    </Container>
  );
};

export default PDFExtractPage; 